import './AutomationPipeline.css';

interface AutomationPipelineProps {
  /** Two-word title; the second word gets the animated selection highlight. */
  titleTop?: string;
  titleBottom?: string;
  className?: string;
}

/**
 * "AI report builder" hero banner — a layered, frosted-glass document editor on
 * an indigo gradient: a sparkle AI badge, a title whose second word is selected
 * by a moving cursor, streaming body text, a formatting toolbar, an image card,
 * an add button and twinkling sparkles. Pure SVG + CSS, reduced-motion aware.
 */
export function AutomationPipeline({
  titleTop = 'Annual',
  titleBottom = 'Report',
  className,
}: AutomationPipelineProps) {
  return (
    <div className={`rpt${className ? ` ${className}` : ''}`}>
      <svg
        className="rpt__svg"
        viewBox="0 0 1024 540"
        role="img"
        aria-label="AI document editor generating an annual report"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <linearGradient id="rptBg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#9aa0f6" />
            <stop offset="50%" stopColor="#7c82ef" />
            <stop offset="100%" stopColor="#5b61e0" />
          </linearGradient>
          <linearGradient id="rptInk" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5b61e0" />
            <stop offset="100%" stopColor="#4338ca" />
          </linearGradient>
          <filter id="rptGrain">
            <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
          </filter>
          <clipPath id="rptSel">
            <rect className="rpt__sel-clip" x="233" y="248" width="262" height="70" />
          </clipPath>
        </defs>

        {/* Background + grain */}
        <rect x="0" y="0" width="1024" height="540" fill="url(#rptBg)" />
        <rect x="0" y="0" width="1024" height="540" filter="url(#rptGrain)" opacity="0.08" />

        {/* ---- Back layered cards ---- */}
        <g className="rpt__float" style={{ ['--d' as string]: '0.6s' }}>
          <rect className="rpt__card rpt__card--ghost" x="70" y="120" width="470" height="400" rx="18" />
        </g>
        <g className="rpt__float" style={{ ['--d' as string]: '0.3s' }}>
          <rect className="rpt__card rpt__card--ghost" x="620" y="30" width="320" height="450" rx="18" />
        </g>

        {/* ---- Right image card ---- */}
        <g className="rpt__float" style={{ ['--d' as string]: '0.9s' }}>
          <rect className="rpt__card rpt__card--soft" x="730" y="55" width="190" height="155" rx="16" />
          <rect className="rpt__imgframe" x="755" y="80" width="140" height="105" rx="8" />
          <circle className="rpt__sun" cx="785" cy="110" r="9" />
          <path className="rpt__mount" d="M758,180 l34,-44 l24,30 l20,-24 l38,38 z" />
        </g>

        {/* ---- Main document card ---- */}
        <g className="rpt__float" style={{ ['--d' as string]: '0s' }}>
          <rect className="rpt__card rpt__card--main" x="150" y="50" width="490" height="465" rx="20" />

          {/* AI sparkle badge */}
          <rect className="rpt__badge" x="178" y="78" width="46" height="46" rx="13" />
          <path className="rpt__badge-spark" d="M201,88 l3,8 l8,3 l-8,3 l-3,8 l-3,-8 l-8,-3 l8,-3 z" />

          {/* toolbar */}
          <rect className="rpt__bar" x="244" y="93" width="210" height="16" rx="8" />
          <rect className="rpt__bar rpt__bar--accent" x="520" y="93" width="74" height="16" rx="8" />
          <circle className="rpt__bar-dot" cx="616" cy="101" r="8" />

          {/* sidebar thumbnails */}
          {[170, 230, 290, 350].map((y, i) => (
            <rect
              key={`th-${i}`}
              className="rpt__thumb"
              x="556"
              y={y}
              width="64"
              height="46"
              rx="8"
              style={{ animationDelay: `${i * 0.25}s` }}
            />
          ))}

          {/* Title */}
          <text className="rpt__title" x="244" y="232">{titleTop}</text>
          {/* selected second word (highlight + white text revealed together) */}
          <g clipPath="url(#rptSel)">
            <rect className="rpt__sel" x="233" y="248" width="262" height="70" rx="6" />
            <text className="rpt__title rpt__title--sel" x="244" y="306">{titleBottom}</text>
          </g>

          {/* cursor */}
          <path className="rpt__cursor" d="M500,330 l40,16 l-16,5 l-5,16 z" />

          {/* streaming body lines */}
          <rect className="rpt__line" x="244" y="372" width="250" height="12" rx="6" style={{ animationDelay: '0s' }} />
          <rect className="rpt__line" x="244" y="398" width="300" height="12" rx="6" style={{ animationDelay: '0.3s' }} />
          <rect className="rpt__line" x="244" y="424" width="190" height="12" rx="6" style={{ animationDelay: '0.6s' }} />
          <rect className="rpt__block" x="244" y="452" width="120" height="48" rx="10" />
        </g>

        {/* ---- Add button ---- */}
        <g className="rpt__float" style={{ ['--d' as string]: '0.5s' }}>
          <circle className="rpt__add" cx="950" cy="250" r="30" />
          <path className="rpt__add-plus" d="M950,236 v28 M936,250 h28" />
        </g>

        {/* ---- Formatting toolbar (three A sizes) ---- */}
        <g className="rpt__float" style={{ ['--d' as string]: '0.4s' }}>
          <rect className="rpt__card rpt__card--soft" x="600" y="395" width="270" height="95" rx="20" />
          {[660, 735, 810].map((cx, i) => (
            <g key={`fa-${i}`} className="rpt__abtn" style={{ ['--d' as string]: `${i * 0.6}s` }}>
              <circle className="rpt__abtn-bg" cx={cx} cy="442" r="28" />
              <text className="rpt__abtn-text" x={cx} y={i === 0 ? 451 : 450} style={{ fontSize: `${20 + i * 6}px` }}>A</text>
              {i === 1 && <rect className="rpt__abtn-underline" x={cx - 11} y="456" width="22" height="3" rx="1.5" />}
            </g>
          ))}
        </g>

        {/* ---- Sparkles ---- */}
        <g className="rpt__sparkles">
          <path className="rpt__star" d="M935,355 l5,13 l13,5 l-13,5 l-5,13 l-5,-13 l-13,-5 l13,-5 z" style={{ ['--d' as string]: '0s' }} />
          <path className="rpt__star" d="M965,300 l3.5,9 l9,3.5 l-9,3.5 l-3.5,9 l-3.5,-9 l-9,-3.5 l9,-3.5 z" style={{ ['--d' as string]: '0.8s' }} />
          <path className="rpt__star" d="M905,150 l3,8 l8,3 l-8,3 l-3,8 l-3,-8 l-8,-3 l8,-3 z" style={{ ['--d' as string]: '1.4s' }} />
        </g>
      </svg>
    </div>
  );
}
